import {Box, Button, Stack} from "@mui/material";
import {UserCreateRequest, UserUpdateRequest, useUserCreateMutation, useUserUpdateMutation} from "../../app/api/api";
import {useFormik} from "formik";
import {useTranslation} from "react-i18next";
import MenuItem from "@mui/material/MenuItem";
import {useSnackbar} from "notistack";
import {useNavigate} from "react-router-dom";
import {PERMISSION_LEVEL_LABEL_KEYS, PermissionLevel, User, UserStatus} from "../../app/api/model/user";
import {getUserValidationSchema} from "./validation";
import FormSelectField from "../../components/input/FormSelectField";
import FormTextInputField from "../../components/input/FormTextField";

export interface UserDetailFormParams {
    /** If user is given, this user is edited. If undefined, a new user will be created. */
    user?: User;
}

export default function UserDetailForm({user}: UserDetailFormParams) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();
    const [updateUser] = useUserUpdateMutation();
    const [createUser] = useUserCreateMutation();
    const isCreateUser = !user;

    const formik = useFormik({
        initialValues: {
            email: user?.email || "",
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            status: user?.status || UserStatus.ACTIVE,
            permissionLevel: user?.permissionLevel || PermissionLevel.VIEW_ONLY,
            password: "",
            passwordConfirmation: "",
        },
        validationSchema: getUserValidationSchema(t, isCreateUser),
        onSubmit: async (values, submitProps) => {
            if (isCreateUser) {
                const requestData = {
                    ...values,
                } as UserCreateRequest;
                const response = await createUser(requestData).unwrap();
                navigate(`/users/${response.id}`);
            } else {
                const requestData = {
                    id: user.id,
                    ...values,
                } as UserUpdateRequest;
                await updateUser(requestData).unwrap();
            }

            enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
            submitProps.resetForm({values});
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <Stack
                spacing={3}
                width={500}
                sx={{
                    mt: 3,
                }}
            >
                <FormTextInputField fieldName="email" label={t("user_email")} form={formik} data-test="userEmail" />
                <FormTextInputField
                    fieldName="firstName"
                    label={t("user_firstName")}
                    form={formik}
                    data-test="userFirstName"
                />
                <FormTextInputField
                    fieldName="lastName"
                    label={t("user_lastName")}
                    form={formik}
                    data-test="userLastName"
                />
                <FormSelectField form={formik} fieldName={"status"} label={t("user_status")} data-test="userStatus">
                    <MenuItem value="ACTIVE">{t("status_active")}</MenuItem>
                    <MenuItem value="INACTIVE">{t("status_inactive")}</MenuItem>
                </FormSelectField>
                <FormSelectField
                    fieldName="permissionLevel"
                    label={t("user_level")}
                    form={formik}
                    onChange={(e) => formik.setFieldValue("permissionLevel", parseInt(e.target.value as string), true)}
                    data-test="userPermissionLevel"
                >
                    {Object.entries(PERMISSION_LEVEL_LABEL_KEYS).map(([value, labelKey]) => (
                        <MenuItem key={value} value={value}>
                            {t(labelKey)}
                        </MenuItem>
                    ))}
                </FormSelectField>
                <FormTextInputField
                    fieldName="password"
                    label={t(isCreateUser ? "user_password" : "user_newPassword")}
                    form={formik}
                    data-test="userPassword"
                />
                <FormTextInputField
                    fieldName="passwordConfirmation"
                    label={t("user_passwordConfirmation")}
                    form={formik}
                    data-test="userPasswordConfirmation"
                />
                {(formik.dirty || isCreateUser) && (
                    <Box sx={{display: "flex", justifyContent: "begin"}}>
                        <Button color="primary" variant="contained" type="submit">
                            {isCreateUser ? t("action_createUser") : t("action_save")}
                        </Button>
                    </Box>
                )}
            </Stack>
        </form>
    );
}
